// Семейство изотерм: основная линия плюс смежные, отбитые от неё шагом в ОБЕ
// стороны до краёв поля кадра.
//
// Почему в обе стороны от основной, а не сеткой от круглого нуля (владелец
// 2026-08-19): капитан ведёт карту ОТ своей рабочей изотермы, и линия, которую
// он выставил ползунком, обязана остаться в наборе ровно там, где стояла.
// Сетка «0, 2, 4…» сдвинула бы её при любом нецелом градусе.
//
// Геометрию и подписи считаем ОДНИМ проходом по уровню. Соседний инструмент
// (waveContours) зовёт contourAtLevel дважды на уровень — там уровней шесть, и
// цена незаметна; здесь их до шести десятков, и второй проход удвоил бы самое
// дорогое место фичи.

import { ISOTHERM_STEP } from './isotherms'
import { contourAtLevel } from '../maps/marchingSquares'
import type { SstGrid } from '../maps/marchingSquares'
import { formatNumber } from '../i18n'
import type { Lang } from '../i18n/types'

/** Границы ползунка шага, °C. Полградуса — то же дробление, что у самой
 *  изотермы: набор «через 0,5» обязан ложиться на её сетку. */
export const ISO_SPREAD_MIN = 0.5
export const ISO_SPREAD_MAX = 5
export const ISO_SPREAD_SLIDER = ISOTHERM_STEP
export const ISO_SPREAD_DEFAULT = 1

/** Потолок линий семейства. 60 при минимальном шаге 0,5 °C — это 30 °C размаха,
 *  больше любого промыслового кадра: в рабочем режиме потолок не срабатывает.
 *  Он от вырожденного случая (поле от кромки льда до Куросио на весь мир), где
 *  сотня линий превращает карту в штриховку, а браузер — в слайд-шоу. */
export const ISO_FAMILY_MAX_LINES = 60

/** Округление к сотым: 12 + 3·0.5 не имеет права дать 12.500000000000002.
 *  Хвост float — это и шум в подписи, и лишний образ в `map.addImage`, который
 *  уже не удалить. */
function round2(value: number): number {
  return Math.round(value * 100) / 100
}

/**
 * Уровни семейства: основная изотерма плюс смежные с шагом `spread` в обе
 * стороны до краёв поля `[vmin, vmax]`. По возрастанию.
 *
 * Основная присутствует ВСЕГДА — даже когда её нет в поле. Это выбор человека,
 * и о том, что линия не легла, говорит легенда («в кадре нет»), а не молчаливо
 * укороченный набор.
 *
 * Диапазон берётся из самой сетки, а не из границ ползунка: «диапазон кадра» —
 * это про данные, которые привезли, а не про то, куда доезжает ползунок.
 */
export function isothermFamilyLevels(
  base: number,
  spread: number | null | undefined,
  vmin: number | null | undefined,
  vmax: number | null | undefined,
): number[] {
  const only = [round2(base)]
  if (spread == null || !(spread > 0)) return only
  if (vmin == null || vmax == null || !Number.isFinite(vmin) || !Number.isFinite(vmax)) return only

  // Ноль (основная) входит всегда, поэтому границы k расширяются до него.
  const kMin = Math.min(Math.ceil((vmin - base) / spread - 1e-9), 0)
  const kMax = Math.max(Math.floor((vmax - base) / spread + 1e-9), 0)

  const ks: number[] = []
  for (let k = kMin; k <= kMax; k++) ks.push(k)

  // Обрезка СИММЕТРИЧНА вокруг основной: дальние уходят первыми. Важна
  // окрестность рабочей изотермы, а не холодный край поля.
  if (ks.length > ISO_FAMILY_MAX_LINES) {
    ks.sort((a, b) => Math.abs(a) - Math.abs(b) || a - b)
    ks.length = ISO_FAMILY_MAX_LINES
    ks.sort((a, b) => a - b)
  }
  return ks.map(k => round2(base + k * spread))
}

export type IsoLineProps = { temp: number; base: boolean; color: string }
export type IsoLabelProps = { temp: number; color: string }

export interface IsothermFamily {
  contours: GeoJSON.FeatureCollection<GeoJSON.MultiLineString, IsoLineProps>
  labels: GeoJSON.FeatureCollection<GeoJSON.Point, IsoLabelProps>
}

/**
 * Линии семейства и точки-подписи к ним по сетке.
 *
 * `base` помечает ОДНУ фичу — ту, что выставил человек. По ней легенда решает,
 * легла ли на карту искомая изотерма: считать по всей коллекции нельзя, иначе
 * нарисованная соседняя линия скроет факт, что 12 °C в кадре нет.
 *
 * Цвет едет свойством фичи, а не краской слоя: одним и тем же кодом рисуются и
 * глубинное семейство, и поверхностная линия другого цвета.
 */
export function buildIsothermFamily(
  grid: SstGrid,
  levels: number[],
  base: number,
  color: string,
  opts?: { labels: boolean },
): IsothermFamily {
  const withLabels = opts?.labels ?? false
  // Разрежение подписей — по осям ПОРОЗНЬ: на балтийской миле ячейка 1/60° ×
  // 1/36°, и общий шаг растянул бы бакеты по одной оси, высыпав подписи по
  // другой.
  const sepLat = Math.max((grid.stepLat ?? grid.step) * 6, 0.3)
  const sepLon = Math.max((grid.stepLon ?? grid.step) * 6, 0.3)

  const contours: GeoJSON.Feature<GeoJSON.MultiLineString, IsoLineProps>[] = []
  const labels: GeoJSON.Feature<GeoJSON.Point, IsoLabelProps>[] = []

  for (const level of levels) {
    const f = contourAtLevel(grid, level)
    contours.push({
      ...f,
      properties: { temp: level, base: level === round2(base), color },
    })
    if (!withLabels) continue
    const seen = new Set<string>()
    for (const [a, b] of f.geometry.coordinates) {
      const lon = (a[0] + b[0]) / 2
      const lat = (a[1] + b[1]) / 2
      const bucket = `${Math.round(lon / sepLon)},${Math.round(lat / sepLat)}`
      if (seen.has(bucket)) continue
      seen.add(bucket)
      labels.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lon, lat] },
        properties: { temp: level, color },
      })
    }
  }

  return {
    contours: { type: 'FeatureCollection', features: contours },
    labels: { type: 'FeatureCollection', features: labels },
  }
}

/** Текст ярлыка на линии: «9,5°» по-русски, «9.5°» по-английски. Хвостовые нули
 *  не дописываются — подпись читают на карте, а не в таблице. */
export function isothermLabelText(temp: number, lang: Lang): string {
  return `${formatNumber(lang, temp)}°`
}

/** Шаг смежных из ссылки: вне диапазона ползунка — «выключено».
 *
 *  Шаг — это РАССТОЯНИЕ: ноль и отрицательное семейства не задают, а девять
 *  градусов ползунок не выдаёт. Мусор из рукотворной ссылки не имеет права
 *  стать линией на карте. */
export function parseIsothermSpread(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value)) return null
  return value >= ISO_SPREAD_MIN && value <= ISO_SPREAD_MAX ? value : null
}

/** Дочернее значение живёт только при включённой основной изотерме.
 *
 *  Оба тумблера — «поверхность» и «смежные» — стоят ВНУТРИ блока изотермы и
 *  вместе с ним исчезают. Ссылка `?isosurf=12` без `iso` дала бы линию, которую
 *  нечем выключить. */
export function childOfIsotherm<T>(isotherm: number | null, value: T | null): T | null {
  return isotherm == null ? null : value
}

/** Какие сетки температуры нужны инструментам прямо сейчас.
 *
 *  Правило одно и стоит отдельно от хука, потому что цена ошибки — лишний
 *  запрос поля на каждый кадр: **поверхность на 0 м читает ТУ ЖЕ сетку**, что и
 *  всё остальное, и второго запроса не заслуживает. Обратное тоже верно:
 *  поверхностная линия при выключенной основной изотерме и выключенных фронтах
 *  всё равно требует глубинную сетку, если Контекст стоит на нуле — иначе
 *  читать ей нечего.
 */
export function temperatureGridPlan(p: {
  isoEnabled: boolean
  frontsEnabled: boolean
  surfaceEnabled: boolean
  depth: number
}): { depthGrid: boolean; surfaceGrid: boolean } {
  return {
    depthGrid: p.isoEnabled || p.frontsEnabled || (p.surfaceEnabled && p.depth === 0),
    surfaceGrid: p.surfaceEnabled && p.depth !== 0,
  }
}
